/**
 * Monthlees header search drawer — predictive search overlay, intent routing, analytics.
 */
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-msd-root]';
  var INPUT_SELECTOR = '[data-msd-input]';
  var SUGGEST_FIELDS =
    'title,body,product_type,tag,variants.title,vendor,variants.sku';
  var BUDGET_RE = /\b(cheap|budget|affordable|under\s+\$?\d+)\b/i;
  var LEGACY_TITLE = ' - member';
  var LEGACY_SUFFIX = '-member';
  // Hardcoded safety net — liquid config can extend/override via queryPins / excludeHandles.
  var DEFAULT_QUERY_PINS = {
    testosterone: ['testosterone-foundation-stack'],
  };
  var DEFAULT_EXCLUDE_HANDLES = ['testosterone-foundation-smart-stack'];
  var MERCH_TYPE_RE = /\b(apparel|merch|merchandise|clothing|tee|t-?shirt|hoodie|onesie|swag|shirt)\b/i;
  var MERCH_QUERY_RE = /\b(apparel|merch|merchandise|clothing|tee|t-?shirt|hoodie|onesie|swag|shirt)\b/i;
  var BEST_SELLERS_COLLECTION = 'best-sellers';
  // Per-keystroke cache so concatenation retries don't double every request.
  var queryResultCache = {};

  function debounce(fn, ms) {
    var timer;
    return function () {
      var args = arguments;
      var ctx = this;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(ctx, args);
      }, ms);
    };
  }

  function djb2(str) {
    var hash = 5381;
    var i;
    for (i = 0; i < str.length; i += 1) {
      hash = (hash * 33) ^ str.charCodeAt(i);
    }
    return (hash >>> 0).toString(16);
  }

  function capture(event, props) {
    if (window.MonthleesAnalytics && typeof window.MonthleesAnalytics.track === 'function') {
      window.MonthleesAnalytics.track(event, props || {});
      return;
    }
    if (window.posthog && typeof window.posthog.capture === 'function') {
      window.posthog.capture(event, props || {});
    }
  }

  function readConfig(root) {
    var node =
      (root && root.querySelector('[data-msd-config]')) ||
      document.querySelector('[data-msd-config]');
    var parsed = {};
    if (node) {
      try {
        parsed = JSON.parse(node.textContent) || {};
      } catch (e) {
        parsed = {};
      }
    }
    return normalizeSearchConfig(parsed);
  }

  function normalizeSearchConfig(config) {
    var next = config || {};
    var pins = Object.assign({}, DEFAULT_QUERY_PINS, next.queryPins || {});
    var exclude = [];
    var seen = {};
    var source = (DEFAULT_EXCLUDE_HANDLES || []).concat(next.excludeHandles || []);
    var i;
    for (i = 0; i < source.length; i += 1) {
      var handle = normalizeHandle(source[i]);
      if (!handle || seen[handle]) continue;
      seen[handle] = true;
      exclude.push(handle);
    }
    next.queryPins = pins;
    next.excludeHandles = exclude;
    return next;
  }

  function normalizeHandle(handle) {
    return String(handle || '')
      .toLowerCase()
      .trim();
  }

  function shouldHideProduct(product, config) {
    var title = (product.title || '').toLowerCase();
    var handle = normalizeHandle(product.handle);
    if (title.indexOf(LEGACY_TITLE) !== -1) return true;
    if (handle.endsWith(LEGACY_SUFFIX)) return true;
    if (handle.indexOf('-pdp') !== -1) return true;

    var excludeHandles = (config && config.excludeHandles) || DEFAULT_EXCLUDE_HANDLES;
    var i;
    for (i = 0; i < excludeHandles.length; i += 1) {
      if (handle && handle === normalizeHandle(excludeHandles[i])) return true;
    }

    var excludeTitleContains = (config && config.excludeTitleContains) || [];
    for (i = 0; i < excludeTitleContains.length; i += 1) {
      var needle = String(excludeTitleContains[i] || '')
        .toLowerCase()
        .trim();
      if (needle && title.indexOf(needle) !== -1) return true;
    }

    return false;
  }

  function productToItem(product) {
    var image =
      (product.featured_image && (product.featured_image.url || product.featured_image)) ||
      product.image;
    var price = product.price;
    // product.js returns cents; suggest.json returns a dollar string.
    if (typeof price === 'number' && price >= 100 && price === Math.floor(price)) {
      price = price / 100;
    }
    var variantMeta = firstVariantMeta(product);
    return {
      type: 'product',
      title: product.title,
      url: product.url || '/products/' + product.handle,
      handle: product.handle,
      image: image,
      price: price,
      productType: product.product_type || product.type || '',
      vendor: product.vendor || '',
      variantId: variantMeta.id || getFirstVariantId(product),
      memberPriceCents: variantMeta.memberPriceCents || 0,
      available: product.available !== false,
      sellingPlanId: null,
      needsHydration: !getFirstVariantId(product),
    };
  }

  function firstVariantMeta(product) {
    var variants = product.variants || [];
    var i;
    for (i = 0; i < variants.length; i += 1) {
      var variant = variants[i];
      if (!variant || typeof variant !== 'object') continue;
      if (variant.available === false) continue;
      var mp = variant.member_price;
      var cents = 0;
      if (mp != null && mp !== '') {
        cents = parseInt(mp, 10) || 0;
      }
      if (variant.id) {
        return { id: variant.id, memberPriceCents: cents };
      }
    }
    var first = variants[0];
    if (first && typeof first === 'object' && first.id) {
      var firstMp = first.member_price;
      return {
        id: first.id,
        memberPriceCents: firstMp != null && firstMp !== '' ? parseInt(firstMp, 10) || 0 : 0,
      };
    }
    return { id: null, memberPriceCents: 0 };
  }

  function queryLooksLikeMerch(query) {
    return MERCH_QUERY_RE.test(String(query || ''));
  }

  function isMerchProduct(item) {
    // Use catalog product_type only — never fall back to item.type ("product").
    var type = String((item && item.productType) || '').trim();
    if (!type) {
      // Store merch SKUs ship with a blank product type; supplements always have one.
      return true;
    }
    return MERCH_TYPE_RE.test(type);
  }

  function rankProductItems(items, query) {
    if (!items || !items.length) return [];
    var allowMerch = queryLooksLikeMerch(query);
    var primary = [];
    var merch = [];
    var i;
    for (i = 0; i < items.length; i += 1) {
      var item = items[i];
      if (!item || item.type !== 'product') continue;
      if (!allowMerch && isMerchProduct(item)) {
        merch.push(item);
      } else {
        primary.push(item);
      }
    }
    // Prefer supplements; only surface merch when the query is about apparel/merch
    // or when nothing else matched (caller may still choose to drop merch).
    return allowMerch ? primary.concat(merch) : primary.length ? primary : [];
  }

  function buildQueryVariants(query) {
    var q = String(query || '').trim();
    if (!q) return [];
    var seen = {};
    var out = [];
    function push(value) {
      var next = String(value || '').trim();
      if (!next) return;
      var key = next.toLowerCase();
      if (seen[key] || key === q.toLowerCase()) return;
      seen[key] = true;
      out.push(next);
    }
    // "go time" → "gotime"
    if (/\s/.test(q)) {
      push(q.replace(/\s+/g, ''));
    }
    // Single token camelCase → "goTime" / "GoTime" → "go time"
    if (!/\s/.test(q) && /[a-z][A-Z]/.test(q)) {
      push(
        q
          .replace(/([a-z])([A-Z])/g, '$1 $2')
          .replace(/\s+/g, ' ')
          .trim()
      );
    }
    return out;
  }

  function matchingPinHandles(query, config) {
    var pins = (config && config.queryPins) || DEFAULT_QUERY_PINS;
    var q = String(query || '')
      .toLowerCase()
      .trim();
    if (!q) return [];
    var matched = [];
    var seen = {};
    var keys = Object.keys(pins);
    var i;
    var j;
    for (i = 0; i < keys.length; i += 1) {
      var key = String(keys[i] || '')
        .toLowerCase()
        .trim();
      if (!key || q.indexOf(key) === -1) continue;
      var handles = pins[keys[i]] || [];
      for (j = 0; j < handles.length; j += 1) {
        var handle = normalizeHandle(handles[j]);
        if (!handle || seen[handle]) continue;
        seen[handle] = true;
        matched.push(handle);
      }
    }
    return matched;
  }

  function productListHasHandle(items, handle) {
    var target = normalizeHandle(handle);
    var i;
    for (i = 0; i < items.length; i += 1) {
      if (items[i] && normalizeHandle(items[i].handle) === target) return true;
    }
    return false;
  }

  function titleMatchesQuery(product, query) {
    var q = String(query || '')
      .toLowerCase()
      .trim();
    if (!q) return true;
    var hay = (
      String(product.title || '') +
      ' ' +
      String(product.handle || '')
    ).toLowerCase();
    var tokens = q.split(/\s+/).filter(function (token) {
      return token.length >= 3;
    });
    if (!tokens.length) return hay.indexOf(q) !== -1;
    return tokens.some(function (token) {
      return hay.indexOf(token) !== -1;
    });
  }

  function fetchProductByHandle(handle) {
    return fetch('/products/' + encodeURIComponent(handle) + '.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    }).then(function (res) {
      if (!res.ok) throw new Error('product');
      return res.json();
    });
  }

  function fetchPinnedItems(query, config) {
    var handles = matchingPinHandles(query, config);
    if (!handles.length) return Promise.resolve([]);
    return Promise.all(
      handles.map(function (handle) {
        return fetchProductByHandle(handle)
          .then(function (product) {
            if (!product || shouldHideProduct(product, config)) return null;
            if (product.available === false) return null;
            return productToItem(product);
          })
          .catch(function () {
            return null;
          });
      })
    ).then(function (items) {
      return items.filter(Boolean);
    });
  }

  function parseSearchProductsSection(html) {
    if (!html) return [];
    var match = String(html).match(
      /<script[^>]*data-msd-search-products[^>]*>([\s\S]*?)<\/script>/i
    );
    if (!match) return [];
    try {
      var parsed = JSON.parse(match[1]);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function fetchSearchFallbackProducts(query) {
    var url =
      '/search?q=' +
      encodeURIComponent(query) +
      '&type=product' +
      '&options[unavailable_products]=hide' +
      '&section_id=monthlees-search-products-json';
    return fetch(url, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('search');
        return res.text();
      })
      .then(parseSearchProductsSection)
      .catch(function () {
        return [];
      });
  }

  function fetchCollectionProducts(handle, limit) {
    if (!handle) return Promise.resolve([]);
    var url =
      '/collections/' +
      encodeURIComponent(handle) +
      '/products.json?limit=' +
      (limit || 6);
    return fetch(url, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then(function (res) {
        if (!res.ok) throw new Error('collection');
        return res.json();
      })
      .then(function (data) {
        return (data && data.products) || [];
      })
      .catch(function () {
        return [];
      });
  }

  function fetchRecoveryProducts(query, config, limit) {
    var intent = detectIntent(query, config);
    var collectionHandle = BEST_SELLERS_COLLECTION;
    if (intent && intent.mode === 'symptom' && intent.url) {
      var match = String(intent.url).match(/\/collections\/([^/?#]+)/i);
      if (match && match[1]) collectionHandle = match[1];
    }
    return fetchCollectionProducts(collectionHandle, limit || 6).then(function (products) {
      var items = [];
      products.forEach(function (product) {
        if (shouldHideProduct(product, config)) return;
        items.push(productToItem(product));
      });
      return rankProductItems(items, query).slice(0, limit || 6);
    });
  }

  function mergeProductItems(pinned, rest, limit) {
    var out = [];
    var seen = {};
    var i;
    function push(item) {
      if (!item || item.type !== 'product') return;
      var handle = normalizeHandle(item.handle);
      var key = handle || String(item.url || item.title || '').toLowerCase();
      if (!key || seen[key]) return;
      seen[key] = true;
      out.push(item);
    }
    for (i = 0; i < pinned.length; i += 1) push(pinned[i]);
    for (i = 0; i < rest.length; i += 1) push(rest[i]);
    return out.slice(0, limit || 6);
  }

  function priceToCents(price) {
    if (price == null || price === '') return null;
    var dollars = parseFloat(String(price).replace(/[^0-9.-]/g, ''));
    if (isNaN(dollars)) return null;
    return Math.round(dollars * 100);
  }

  function formatMoney(cents, format) {
    if (cents == null) return '';
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, format || '${{amount}}');
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function formatSuggestPrice(price, format) {
    return formatMoney(priceToCents(price), format);
  }

  function buildSuggestUrl(query, limit) {
    return (
      '/search/suggest.json?q=' +
      encodeURIComponent(query) +
      '&resources[type]=product,article,page' +
      '&resources[limit]=' +
      limit +
      '&resources[options][unavailable_products]=hide' +
      '&resources[options][fields]=' +
      encodeURIComponent(SUGGEST_FIELDS)
    );
  }

  function getFirstVariantId(product) {
    var variants = product.variants || [];
    var i;
    for (i = 0; i < variants.length; i += 1) {
      var variant = variants[i];
      if (!variant) continue;
      // Predictive Search sometimes returns bare ids; product.js returns objects.
      if (typeof variant === 'number' || typeof variant === 'string') return variant;
      if (variant.available !== false && variant.id) return variant.id;
    }
    var first = variants[0];
    if (!first) return null;
    return typeof first === 'object' ? first.id : first;
  }

  function firstSellingPlanId(product) {
    var groups = (product && product.selling_plan_groups) || [];
    var i;
    var j;
    for (i = 0; i < groups.length; i += 1) {
      var plans = groups[i].selling_plans || [];
      for (j = 0; j < plans.length; j += 1) {
        if (plans[j] && plans[j].id) return plans[j].id;
      }
    }
    return null;
  }

  function hydrateProductForCart(handle) {
    return fetch('/products/' + encodeURIComponent(handle) + '.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then(function (res) {
        if (!res.ok) throw new Error('product');
        return res.json();
      })
      .then(function (product) {
        var variants = product.variants || [];
        var variant =
          variants.find(function (v) {
            return v.available;
          }) || variants[0];
        return {
          variantId: variant && variant.id,
          available: !!(variant && variant.available),
          sellingPlanId: firstSellingPlanId(product),
          price: variant ? variant.price / 100 : null,
        };
      });
  }

  function collectItems(data, config, options) {
    var products =
      (data.resources && data.resources.results && data.resources.results.products) || [];
    var articles =
      (data.resources && data.resources.results && data.resources.results.articles) || [];
    var pages =
      (data.resources && data.resources.results && data.resources.results.pages) || [];
    var items = [];
    var productsOnly = options && options.productsOnly;
    var limit = (options && options.limit) || config.suggestLimit || 6;

    products.forEach(function (product) {
      if (shouldHideProduct(product, config)) return;
      items.push(productToItem(product));
    });

    if (!productsOnly) {
      articles.forEach(function (article) {
        items.push({
          type: 'article',
          title: article.title,
          url: article.url,
          image: article.featured_image && article.featured_image.url,
        });
      });

      pages.forEach(function (page) {
        items.push({
          type: 'page',
          title: page.title,
          url: page.url,
        });
      });
    }

    return items.slice(0, limit);
  }

  function detectIntent(query, config) {
    if (!config.enableIntent) return { mode: 'product' };
    var q = query.toLowerCase().trim();

    if (BUDGET_RE.test(q)) {
      return { mode: 'budget' };
    }

    var lexicon = config.symptomLexicon || {};
    var keys = Object.keys(lexicon);
    var i;
    for (i = 0; i < keys.length; i += 1) {
      if (q.indexOf(keys[i]) !== -1) {
        return {
          mode: 'symptom',
          concern: lexicon[keys[i]].concern,
          url: lexicon[keys[i]].url,
        };
      }
    }

    return { mode: 'product' };
  }

  function sortByPrice(items) {
    return items.slice().sort(function (a, b) {
      var pa = priceToCents(a.price) || 99999999;
      var pb = priceToCents(b.price) || 99999999;
      return pa - pb;
    });
  }

  function openCartDrawer() {
    if (window.MonthleesCartDrawer) {
      if (typeof MonthleesCartDrawer.refresh === 'function') {
        return MonthleesCartDrawer.refresh({ force: true }).then(function () {
          if (typeof MonthleesCartDrawer.show === 'function') MonthleesCartDrawer.show();
          else if (typeof MonthleesCartDrawer.open === 'function') {
            MonthleesCartDrawer.open({ skipFetch: true });
          }
        });
      }
    }
    window.location.href = '/cart';
  }

  function initDrawer(root) {
    var config = readConfig(root);
    if (window.MonthleesBoldPricing && typeof MonthleesBoldPricing.primeMemberIndex === 'function') {
      MonthleesBoldPricing.primeMemberIndex();
    }
    var input = root.querySelector(INPUT_SELECTOR);
    var launchpad = root.querySelector('[data-msd-launchpad]');
    var results = root.querySelector('[data-msd-results]');
    var intentEl = root.querySelector('[data-msd-intent]');
    var zero = root.querySelector('[data-msd-zero]');
    var footer = root.querySelector('[data-msd-footer]');
    var seeAll = root.querySelector('[data-msd-see-all]');
    var form = root.querySelector('[data-msd-form]');
    var lastFocus = null;
    var currentQuery = '';
    var currentIntent = { mode: 'product' };

    if (!input || !results) return;

    function setPanelState(state) {
      var hasQuery = state === 'results' || state === 'zero';
      launchpad.hidden = hasQuery;
      results.hidden = state !== 'results';
      zero.hidden = state !== 'zero';
      footer.hidden = state !== 'results';
      intentEl.hidden = state !== 'results' || !intentEl.innerHTML;
    }

    function renderIntentBanner(intent) {
      intentEl.innerHTML = '';
      if (!config.enableIntent || intent.mode === 'product') {
        intentEl.hidden = true;
        return;
      }

      var banner = document.createElement('div');
      banner.className = 'msd__intent-banner';

      if (intent.mode === 'symptom') {
        banner.classList.add('msd__intent-banner--symptom');
        banner.innerHTML =
          '<p class="msd__intent-label">Sounds like · ' +
          escapeHtml(intent.concern || 'Concern') +
          '</p>' +
          '<p class="msd__intent-copy">Trouble with ' +
          escapeHtml(intent.concern || 'this') +
          '? Browse products for this concern — or get the guided answer.</p>' +
          '<a class="msd__intent-link" href="' +
          escapeAttr(config.assessmentUrl || '/pages/precision-assessment') +
          '">Build my pack →</a>';
      } else if (intent.mode === 'budget') {
        banner.classList.add('msd__intent-banner--budget');
        banner.innerHTML =
          '<p class="msd__intent-label">Budget-friendly picks</p>' +
          '<p class="msd__intent-copy">Sorted by price. Membership unlocks member pricing on every product.</p>' +
          '<a class="msd__intent-link" href="' +
          escapeAttr(config.membershipUrl || '/pages/member-hub') +
          '">Join →</a>';
      }

      intentEl.appendChild(banner);
      intentEl.hidden = false;
    }

    function escapeHtml(str) {
      return String(str || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }

    function escapeAttr(str) {
      return escapeHtml(str).replace(/'/g, '&#39;');
    }

    /*
     * Entity rows — foods, spices, ingredients, biomarkers, goals, routines,
     * topics. Shopify's predictive endpoint is asked for
     * `resources[type]=product,article,page` and cannot return metaobjects at all,
     * so without this the drawer finds nothing for "ferritin" or "berbere" no
     * matter how well the rest of it ranks.
     *
     * Scoring, synonyms and row markup come from monthlees-omnisearch.js so the
     * drawer, /search and the standalone harness cannot disagree about one query.
     *
     * Everything here is defensive on purpose: if the script is absent or the index
     * fetch fails, the drawer's own results are left exactly as they were.
     */
    function injectEntities(query, hadItems) {
      var api = window.MonthleesEntitySearch;
      if (!api || !api.html) return;
      api.html(query, api.ENTITY_TYPES, 3).then(function (html) {
        if (!html) return;
        // A later keystroke may have already rendered. Dropping a stale response is
        // the whole reason currentQuery is captured before the await.
        if (query !== currentQuery) return;
        var box = document.createElement('div');
        box.className = 'msd__entities';
        box.innerHTML = html;
        results.insertBefore(box, results.firstChild);
        // The predictive endpoint found nothing, but we did — so the zero panel is
        // now wrong and would tell the reader there is no ferritin page while
        // holding a link to it.
        if (!hadItems) setPanelState('results');
      });
    }

    function renderResults(items, query, options) {
      results.innerHTML = '';
      currentQuery = query;
      currentIntent = detectIntent(query, config);
      var opts = options || {};
      var isRecovery = !!opts.recovery;
      capture('search_intent', {
        mode: currentIntent.mode,
        query_hash: djb2(query),
        source: 'header_drawer',
      });
      capture('search_performed', {
        query: query,
        result_count: (items && items.length) || 0,
      });
      renderIntentBanner(currentIntent);

      if (!items.length) {
        var zeroMsg = root.querySelector('[data-msd-zero-message]');
        if (zeroMsg) {
          zeroMsg.textContent =
            'No matches for "' +
            query +
            '" — but that\'s not a dead end. Tell the assessment what you\'re trying to fix.';
        }
        capture('search_zero_results', {
          query_hash: djb2(query),
          source: 'header_drawer',
        });
        setPanelState('zero');
        injectEntities(query, false);
        return;
      }

      if (isRecovery) {
        var recoveryNote = document.createElement('p');
        recoveryNote.className = 'msd__recovery-note';
        recoveryNote.textContent =
          'No exact matches for "' + query + '" — closest picks to keep you moving:';
        results.appendChild(recoveryNote);
      }

      var displayItems = items;
      if (currentIntent.mode === 'budget') {
        displayItems = sortByPrice(items.filter(function (item) {
          return item.type === 'product';
        }));
        var nonProducts = items.filter(function (item) {
          return item.type !== 'product';
        });
        displayItems = displayItems.concat(nonProducts);
      }

      displayItems.forEach(function (item, index) {
        var row = document.createElement('div');
        row.className = 'msd__row' + (item.type !== 'product' ? ' msd__row--content' : '');
        row.setAttribute('role', 'option');

        if (item.image) {
          var media = document.createElement('div');
          media.className = 'msd__row-media';
          var img = document.createElement('img');
          img.src = item.image;
          img.alt = '';
          img.loading = 'lazy';
          media.appendChild(img);
          row.appendChild(media);
        } else if (item.type !== 'page') {
          var placeholder = document.createElement('div');
          placeholder.className = 'msd__row-media';
          placeholder.setAttribute('aria-hidden', 'true');
          row.appendChild(placeholder);
        }

        var link = document.createElement('a');
        link.className = 'msd__row-link';
        link.href = item.url;
        link.addEventListener('click', function () {
          capture('search_suggestion_click', {
            query_hash: djb2(query),
            position: index + 1,
            object_type: item.type,
            handle: item.handle || '',
            source: 'header_drawer',
          });
        });

        var body = document.createElement('div');
        body.className = 'msd__row-body';
        var title = document.createElement('p');
        title.className = 'msd__row-title';
        title.textContent = item.title;
        body.appendChild(title);

        if (item.type === 'article') {
          var articleMeta = document.createElement('p');
          articleMeta.className = 'msd__row-meta';
          articleMeta.textContent = 'Article';
          body.appendChild(articleMeta);
        } else if (item.type === 'page') {
          var pageMeta = document.createElement('p');
          pageMeta.className = 'msd__row-meta';
          pageMeta.textContent = 'Page';
          body.appendChild(pageMeta);
        }

        link.appendChild(body);
        row.appendChild(link);

        if (item.type === 'product') {
          var actions = document.createElement('div');
          actions.className = 'msd__row-actions';

          var price = document.createElement('p');
          price.className = 'msd__row-price';
          var guestCents = priceToCents(item.price);
          var guestText = formatMoney(guestCents, config.moneyFormat);
          var memberText = '';
          var memberCents = parseInt(item.memberPriceCents, 10) || 0;
          if (
            !memberCents &&
            window.MonthleesBoldPricing &&
            typeof MonthleesBoldPricing.resolveMemberPreview === 'function' &&
            item.variantId
          ) {
            var preview = MonthleesBoldPricing.resolveMemberPreview(item.variantId, guestCents, {
              moneyFormat: config.moneyFormat,
              metafieldCents: 0,
              allowRatio: false,
            });
            if (preview && preview.boldApplied && preview.cents && preview.source !== 'ratio') {
              memberCents = preview.cents;
              memberText = preview.text;
            }
          } else if (memberCents > 0) {
            memberText = formatMoney(memberCents, config.moneyFormat);
          }
          if (memberText && guestText && memberText !== guestText) {
            price.innerHTML =
              '<span class="msd__row-price-member">' +
              memberText +
              '</span><span class="msd__row-price-chip">MEMBER</span><span class="msd__row-price-guest">' +
              guestText +
              '</span>';
          } else {
            price.textContent = guestText || formatSuggestPrice(item.price, config.moneyFormat);
          }
          actions.appendChild(price);

          var addBtn = document.createElement('button');
          addBtn.type = 'button';
          addBtn.className = 'msd__add';
          addBtn.textContent = 'Add';
          // Suggest.json often omits variants — enable immediately and hydrate on click.
          addBtn.disabled = item.available === false;
          if (!item.variantId && item.handle) {
            hydrateProductForCart(item.handle)
              .then(function (hydrated) {
                item.variantId = hydrated.variantId;
                item.sellingPlanId = hydrated.sellingPlanId;
                item.available = hydrated.available;
                addBtn.disabled = !item.variantId || item.available === false;
              })
              .catch(function () {
                /* keep clickable; click path re-hydrates */
              });
          }
          addBtn.addEventListener('click', function (event) {
            event.preventDefault();
            event.stopPropagation();
            addBtn.disabled = true;

            var prepare = item.variantId
              ? Promise.resolve({
                  variantId: item.variantId,
                  sellingPlanId: item.sellingPlanId,
                  available: item.available !== false,
                })
              : hydrateProductForCart(item.handle);

            prepare
              .then(function (hydrated) {
                if (!hydrated.variantId || hydrated.available === false) {
                  throw new Error('unavailable');
                }
                item.variantId = hydrated.variantId;
                item.sellingPlanId = hydrated.sellingPlanId;
                var payloadItem = {
                  id: Number(hydrated.variantId),
                  quantity: 1,
                };
                if (hydrated.sellingPlanId) {
                  payloadItem.selling_plan = Number(hydrated.sellingPlanId);
                }
                return fetch('/cart/add.js', {
                  method: 'POST',
                  credentials: 'same-origin',
                  headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                  },
                  body: JSON.stringify({ items: [payloadItem] }),
                });
              })
              .then(function (res) {
                if (!res.ok) throw new Error('add');
                return res.json();
              })
              .then(function () {
                capture('search_add_to_cart', {
                  query_hash: djb2(query),
                  product_id: item.variantId,
                  source: 'header_drawer',
                  purchase_type: item.sellingPlanId ? 'subscription' : 'one_time',
                });
                closeDrawer();
                return openCartDrawer();
              })
              .catch(function () {
                addBtn.disabled = false;
              });
          });
          actions.appendChild(addBtn);
          row.appendChild(actions);
        }

        results.appendChild(row);
      });

      if (seeAll) {
        seeAll.href = '/search?q=' + encodeURIComponent(query);
      }

      setPanelState('results');
      injectEntities(query, true);
    }

    function showLaunchpad() {
      currentQuery = '';
      results.innerHTML = '';
      intentEl.innerHTML = '';
      intentEl.hidden = true;
      if (footer) footer.hidden = true;
      setPanelState('launchpad');
    }

    function resetPanel() {
      input.value = '';
      showLaunchpad();
    }

    function trapFocus(event) {
      if (!root.classList.contains('is-open')) return;
      if (event.key !== 'Tab') return;
      var focusable = root.querySelectorAll(
        'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    function openDrawer() {
      lastFocus = document.activeElement;
      root.hidden = false;
      root.setAttribute('aria-hidden', 'false');
      requestAnimationFrame(function () {
        root.classList.add('is-open');
      });
      document.documentElement.classList.add('msd-open');
      resetPanel();
      setTimeout(function () {
        input.focus();
      }, 50);
    }

    function closeDrawer() {
      root.classList.remove('is-open');
      root.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('msd-open');
      setTimeout(function () {
        root.hidden = true;
        resetPanel();
        if (lastFocus && typeof lastFocus.focus === 'function') {
          lastFocus.focus();
        }
      }, 220);
    }

    function resolveQueryProducts(query, limit) {
      var cacheKey = query.toLowerCase();
      if (queryResultCache[cacheKey]) {
        return queryResultCache[cacheKey];
      }

      var suggestPromise = fetch(buildSuggestUrl(query, limit), {
        credentials: 'same-origin',
      }).then(function (res) {
        return res.json();
      });
      var pinPromise = fetchPinnedItems(query, config);

      var pending = Promise.all([suggestPromise, pinPromise])
        .then(function (parts) {
          var data = parts[0] || {};
          var pinned = parts[1] || [];
          var pinHandles = matchingPinHandles(query, config);
          var suggestItems = collectItems(data, config, { limit: limit * 2 });
          var suggestProducts = rankProductItems(
            suggestItems.filter(function (item) {
              return item.type === 'product';
            }),
            query
          );
          var contentRows = suggestItems.filter(function (item) {
            return item.type !== 'product';
          });

          var pinsMissingFromSuggest =
            pinHandles.length > 0 &&
            pinHandles.some(function (handle) {
              return !productListHasHandle(suggestProducts, handle);
            });
          var weakSuggest =
            suggestProducts.length > 0 &&
            pinHandles.length > 0 &&
            !suggestProducts.some(function (item) {
              return titleMatchesQuery(item, query);
            });
          // suggest.json usually omits variants — without them we cannot read
          // monthlees.member_price. Prefer the section fallback in that case.
          var suggestLacksVariants =
            suggestProducts.length > 0 &&
            suggestProducts.every(function (item) {
              return !item.variantId;
            });
          var useFallback =
            suggestProducts.length === 0 ||
            pinsMissingFromSuggest ||
            weakSuggest ||
            suggestLacksVariants;

          var restPromise = Promise.resolve(suggestProducts);
          if (useFallback) {
            restPromise = fetchSearchFallbackProducts(query).then(function (products) {
              var fallbackItems = [];
              products.forEach(function (product) {
                if (shouldHideProduct(product, config)) return;
                fallbackItems.push(productToItem(product));
              });
              fallbackItems = rankProductItems(fallbackItems, query);
              if (!fallbackItems.length) {
                return suggestProducts.filter(function (item) {
                  return titleMatchesQuery(item, query);
                });
              }
              return fallbackItems;
            });
          }

          return restPromise.then(function (productRest) {
            var mergedProducts = mergeProductItems(pinned, productRest, limit);
            return {
              products: mergedProducts,
              content: contentRows,
            };
          });
        })
        .catch(function () {
          return { products: [], content: [] };
        });

      queryResultCache[cacheKey] = pending;
      return pending;
    }

    var fetchSuggestions = debounce(function (query) {
      // Keep whatever the user typed — only wipe the field on open/close.
      if (!query || query.length < 2) {
        showLaunchpad();
        return;
      }

      var limit = config.suggestLimit || 6;

      resolveQueryProducts(query, limit)
        .then(function (primary) {
          var products = (primary && primary.products) || [];
          var contentRows = (primary && primary.content) || [];
          if (products.length) {
            return { products: products, content: contentRows, recovery: false };
          }

          // Zero products: retry once with concatenation / camelCase variants.
          var variants = buildQueryVariants(query);
          if (!variants.length) {
            return { products: [], content: contentRows, recovery: false };
          }

          return resolveQueryProducts(variants[0], limit).then(function (alt) {
            var altProducts = (alt && alt.products) || [];
            if (altProducts.length) {
              return {
                products: altProducts,
                content: (alt && alt.content) || contentRows,
                recovery: false,
              };
            }
            return { products: [], content: contentRows, recovery: false };
          });
        })
        .then(function (resolved) {
          var products = (resolved && resolved.products) || [];
          var contentRows = (resolved && resolved.content) || [];
          if (products.length) {
            return {
              items: products.concat(contentRows).slice(0, limit),
              recovery: false,
            };
          }

          // Still empty — recover with closest collection picks (not a dead end).
          return fetchRecoveryProducts(query, config, limit).then(function (recovery) {
            if (recovery && recovery.length) {
              return { items: recovery, recovery: true };
            }
            return { items: contentRows.slice(0, limit), recovery: false };
          });
        })
        .then(function (payload) {
          var items = (payload && payload.items) || [];
          var ready = Promise.resolve();
          if (
            window.MonthleesBoldPricing &&
            typeof MonthleesBoldPricing.primeMemberIndex === 'function'
          ) {
            ready = MonthleesBoldPricing.primeMemberIndex();
          }
          return ready.then(function () {
            renderResults(items, query, { recovery: !!(payload && payload.recovery) });
          });
        })
        .catch(function () {
          setPanelState('zero');
        });
    }, 220);

    input.addEventListener('input', function () {
      fetchSuggestions(input.value.trim());
    });

    root.querySelectorAll('[data-msd-close]').forEach(function (el) {
      el.addEventListener('click', closeDrawer);
    });

    root.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeDrawer();
      }
    });

    document.addEventListener('keydown', trapFocus);

    if (form) {
      form.addEventListener('submit', function (event) {
        var query = input.value.trim();
        if (!query) {
          event.preventDefault();
          return;
        }
        capture('internal_search', {
          query_hash: djb2(query),
          source: 'header_drawer',
        });
      });
    }

    if (seeAll) {
      seeAll.addEventListener('click', function () {
        capture('internal_search', {
          query_hash: djb2(currentQuery || input.value.trim()),
          source: 'header_drawer',
        });
      });
    }

    root.querySelectorAll('[data-msd-chip]').forEach(function (chip) {
      chip.addEventListener('click', function () {
        var term = chip.getAttribute('data-msd-chip') || chip.textContent.trim();
        input.value = term;
        fetchSuggestions(term);
        input.focus();
      });
    });

    document.addEventListener('click', function (event) {
      var link = event.target.closest('.header__search-link, [data-msd-open]');
      if (link) {
        event.preventDefault();
        openDrawer();
        return;
      }
    });

    document.addEventListener('keydown', function (event) {
      var isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      var mod = isMac ? event.metaKey : event.ctrlKey;
      if (mod && event.key.toLowerCase() === 'k') {
        var tag = (event.target && event.target.tagName) || '';
        if (tag === 'INPUT' || tag === 'TEXTAREA' || event.target.isContentEditable) return;
        event.preventDefault();
        if (root.classList.contains('is-open')) closeDrawer();
        else openDrawer();
      }
    });

    window.MonthleesSearchDrawer = {
      open: openDrawer,
      close: closeDrawer,
    };
  }

  function init() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(initDrawer);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
